/**
 * PDFDocumentViewer
 *
 * PDF viewer component with bounding box overlay support.
 * Captures page dimensions and renders citation highlights.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import type { Citation, PageDimensions } from '../types';
import { BoundingBoxOverlay } from './BoundingBoxOverlay';
import { useDocumentComparison } from '../hooks/useDocumentComparison';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PDFDocumentViewerProps {
  /** Column ID for state management */
  columnId: string;
  /** URL to the PDF document */
  url: string;
  /** All citations for this document */
  citations: Citation[];
  /** Map from citation to field path */
  citationFieldMap: Map<Citation, string>;
}

export function PDFDocumentViewer({
  columnId,
  url,
  citations,
  citationFieldMap,
}: PDFDocumentViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Local state
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number>(500);
  const [pageDimensions, setPageDimensionsLocal] = useState<
    Record<number, PageDimensions>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Context
  const {
    hoveredFieldPath,
    lockedFieldPath,
    setHoveredField,
    setHoveredCitation,
    setLockedField,
    setPageDimensions: setContextPageDimensions,
    getCitationsForField,
  } = useDocumentComparison();

  // Measure container width for responsive sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      // Get the actual available width (the container is constrained by its parent)
      const rect = container.getBoundingClientRect();
      const width = rect.width - 32; // 16px padding on each side for the overflow area
      if (width > 0) {
        setContainerWidth(Math.max(200, width));
      }
    };

    // Initial measurement
    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(updateWidth);
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Auto-navigate to page when a field is hovered
  useEffect(() => {
    if (hoveredFieldPath) {
      const fieldCitations = getCitationsForField(hoveredFieldPath);
      if (fieldCitations.length > 0) {
        const targetPage = fieldCitations[0].page;
        if (targetPage !== pageNumber) {
          setPageNumber(targetPage);
        }
      }
    }
  }, [hoveredFieldPath, getCitationsForField, pageNumber]);

  // Handlers
  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setIsLoading(false);
      setError(null);
    },
    [],
  );

  const onDocumentLoadError = useCallback((err: Error) => {
    Sentry.captureException(err);
    console.error('PDF load error:', err);
    setError(err);
    setIsLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback(
    (page: { width: number; height: number; pageNumber: number }) => {
      const dimensions: PageDimensions = {
        width: page.width,
        height: page.height,
      };
      setPageDimensionsLocal((prev) => ({
        ...prev,
        [page.pageNumber]: dimensions,
      }));
      setContextPageDimensions(columnId, page.pageNumber, dimensions);
    },
    [columnId, setContextPageDimensions],
  );

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
  }, [numPages]);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleCitationHover = useCallback(
    (citation: Citation | null, fieldPath: string | null) => {
      setHoveredCitation(citation);
      // Also set the field as hovered for bi-directional highlighting
      if (fieldPath) {
        setHoveredField(fieldPath);
      }
    },
    [setHoveredCitation, setHoveredField],
  );

  const handleCitationClick = useCallback(
    (_citation: Citation, fieldPath: string) => {
      setLockedField(fieldPath);
    },
    [setLockedField],
  );

  const currentPageDimensions = pageDimensions[pageNumber];
  const scaledWidth = containerWidth * scale;

  return (
    <div ref={containerRef} className="flex flex-col h-full w-full">
      {/* Controls Header */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 p-2 border-b bg-muted/30">
        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[80px] text-center">
            {pageNumber} / {numPages || '?'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextPage}
            disabled={!numPages || pageNumber >= numPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 w-full">
        {error ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-destructive mb-2">Error loading PDF</p>
            <p className="text-sm text-muted-foreground">
              Please try downloading the file instead.
            </p>
          </div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex justify-center items-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <div
              className="relative"
              style={{ width: scaledWidth, maxWidth: '100%', margin: '0 auto' }}
            >
              <Page
                key={`page-${pageNumber}-${containerWidth}`}
                pageNumber={pageNumber}
                width={scaledWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={onPageLoadSuccess}
                loading={
                  <div className="flex justify-center items-center min-h-[400px]">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                }
              />

              {/* Bounding Box Overlay */}
              {currentPageDimensions && (
                <BoundingBoxOverlay
                  citations={citations}
                  pageDimensions={currentPageDimensions}
                  currentPage={pageNumber}
                  hoveredFieldPath={hoveredFieldPath}
                  lockedFieldPath={lockedFieldPath}
                  citationFieldMap={citationFieldMap}
                  onCitationHover={handleCitationHover}
                  onCitationClick={handleCitationClick}
                  scale={scale}
                />
              )}
            </div>
          </Document>
        )}
      </div>
    </div>
  );
}
